import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { formatTimestamp } from '../lib/history';
import { cn } from '../lib/utils';
import type { PipelineStepStatus } from '../types.ts';

interface PipelineArrowProps {
  label: string;
  status: PipelineStepStatus;
  version?: string;
  date?: string;
  disabledReason?: 'environment_disabled';
  title?: string;
  warningTitle?: string;
  className?: string;
  compact?: boolean;
  isFirst?: boolean; // Whether this is the first arrow (straight left edge)
  onClick?: () => void; // Click handler for interactive arrows
  clickable?: boolean; // Whether the arrow should be clickable
}

const statusColors: Record<PipelineStepStatus, { bg: string; text: string }> = {
  disabled: { bg: 'bg-gray-100', text: 'text-gray-400' },
  not_deployed: { bg: 'bg-background', text: 'text-gray-400' },
  success: { bg: '', text: 'text-white' }, // Custom color
  warning: { bg: 'bg-gray-400', text: 'text-white' },
  error: { bg: '', text: 'text-white' }, // Custom color
  pending: { bg: '', text: 'text-white' }, // Custom color
};

const customColors: Record<string, string> = {
  success: '#244554',
  pending: '#8FC5BE',
  error: '#D96243',
};

export const PipelineArrow = ({
  label,
  status,
  version,
  date,
  disabledReason,
  title: titleOverride,
  warningTitle,
  className,
  compact = false,
  isFirst = false,
  onClick,
  clickable = false,
}: PipelineArrowProps) => {
  // Generate stable random values for this component instance
  const [animationDelay] = useState(() => Math.random() * 1500); // 0-500ms
  const [animationDuration] = useState(() => 1800 + Math.random() * 700); // 1800-2500ms

  const colors = statusColors[status];
  const isEnvironmentDisabled = status === 'disabled' && disabledReason === 'environment_disabled';
  const customBg = customColors[status];
  const isPending = status === 'pending';
  const isNotDeployed = status === 'not_deployed';
  const statusLabel = isEnvironmentDisabled ? 'Disabled' : undefined;
  const title =
    titleOverride ??
    (date !== undefined
      ? formatTimestamp(date)
      : isEnvironmentDisabled
        ? 'This stage is disabled for this app'
        : isNotDeployed
          ? 'No deployment has run for this stage yet'
          : undefined);

  // Different clip paths for first arrow (straight left) vs others (notched left)
  const clipPath = isFirst
    ? 'polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%)'
    : 'polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%, 20px 50%)';
  const diagonalBorderLength = Math.sqrt(20 * 20 + 32 * 32);
  const diagonalBorderAngle = `${Math.atan2(32, 20)}rad`;

  return (
    <div className={cn('relative flex items-center', compact ? 'flex-[0.5]' : 'flex-1', className)}>
      {/* Arrow body */}
      <div
        className={cn(
          'relative flex h-16 w-full items-center justify-center transition-colors overflow-hidden',
          isFirst ? 'pl-3 pr-6' : 'px-6',
          isNotDeployed ? 'bg-background' : colors.bg,
          colors.text,
          isEnvironmentDisabled && 'opacity-0',
          clickable && !isEnvironmentDisabled && 'cursor-pointer hover:opacity-90',
        )}
        style={{
          clipPath,
          backgroundColor: customBg || undefined,
          backgroundImage: isEnvironmentDisabled
            ? 'repeating-linear-gradient(135deg, rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 8px, transparent 8px, transparent 16px)'
            : undefined,
        }}
        onClick={clickable && !isEnvironmentDisabled ? onClick : undefined}
        title={isEnvironmentDisabled ? undefined : title}
      >
        {/* Shimmer effect for pending state - repeating wave pattern */}
        {isPending && (
          <div
            className='absolute inset-0'
            style={{
              background:
                'repeating-linear-gradient(90deg, transparent 0%, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%, transparent 100%)',
              backgroundSize: '200px 100%',
              animation: `shimmer ${animationDuration}ms linear infinite`,
              animationDelay: `${animationDelay}ms`,
            }}
          />
        )}

        <div className='relative z-10 flex flex-col items-center gap-0.5 text-center'>
          <span className='flex items-center gap-1.5 text-sm font-medium'>
            {isEnvironmentDisabled && <NoSymbolIcon className='h-4 w-4' />}
            {label}
          </span>
          {warningTitle && !isEnvironmentDisabled && (
            <span
              className='text-[11px] font-medium leading-none text-white/70'
              title={warningTitle}
            >
              Sensitive
            </span>
          )}
          {version && <span className='text-xs opacity-90'>{version}</span>}
          {statusLabel && <span className='text-xs font-semibold uppercase'>{statusLabel}</span>}
        </div>
      </div>

      {isNotDeployed && !isEnvironmentDisabled && (
        <div className='pointer-events-none absolute inset-0 z-20 h-16 overflow-visible'>
          <span className='absolute left-0 right-5 top-0 h-px bg-gray-200' />
          <span className='absolute bottom-0 left-0 right-5 h-px bg-gray-200' />
          <span
            className='absolute top-0 h-px origin-top-left bg-gray-200'
            style={{
              left: 'calc(100% - 20px)',
              width: `${diagonalBorderLength}px`,
              transform: `rotate(${diagonalBorderAngle})`,
            }}
          />
          <span
            className='absolute bottom-0 h-px origin-bottom-left bg-gray-200'
            style={{
              left: 'calc(100% - 20px)',
              width: `${diagonalBorderLength}px`,
              transform: `rotate(-${diagonalBorderAngle})`,
            }}
          />
          {!isFirst && (
            <>
              <span
                className='absolute left-0 top-0 h-px origin-top-left bg-gray-200'
                style={{
                  width: `${diagonalBorderLength}px`,
                  transform: `rotate(${diagonalBorderAngle})`,
                }}
              />
              <span
                className='absolute bottom-0 left-0 h-px origin-bottom-left bg-gray-200'
                style={{
                  width: `${diagonalBorderLength}px`,
                  transform: `rotate(-${diagonalBorderAngle})`,
                }}
              />
            </>
          )}
          {isFirst && <span className='absolute bottom-0 left-0 top-0 w-px bg-gray-200' />}
        </div>
      )}

      <style>{`
        @keyframes shimmer {
          0% {
            background-position: 0px 0%;
          }
          100% {
            background-position: 200px 0%;
          }
        }
      `}</style>
    </div>
  );
};
